<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div v-if="isFeatureEnabled('uifeatures')">
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-clone"></i> {{ _('Ready-made screens') }}</h3>
          <p class="text-muted">{{ _('Add a complete menu section with the components already in place.') }}</p>
        </div>
      </div>
      <p v-if="loading && !features.length" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="!features.length" class="text-muted">{{ _('No ready-made screens are available yet.') }}</p>
      <div v-else class="feature-grid">
        <article
          v-for="feature in features"
          :key="feature.id"
          class="feature-card"
          :class="{ 'is-added': feature.on_current_ui }"
        >
          <div class="feature-card__icon" aria-hidden="true">
            <i :class="'fa fa-fw fa-2x fa-' + (feature.icon || 'puzzle-piece')"></i>
          </div>
          <h4 class="feature-card__title">{{ feature.title }}</h4>
          <p class="feature-card__description">{{ feature.description }}</p>
          <div class="feature-card__actions">
            <template v-if="feature.on_current_ui">
              <span class="label label-default">{{ _('Added') }}</span>
              <button
                v-if="canModifyLayouts && hasOtherClients"
                type="button"
                class="btn btn-default btn-sm"
                v-on:click="openPlace(feature, true)"
              >
                {{ _('Add to another UI') }}
              </button>
            </template>
            <button
              v-else-if="canModifyLayouts"
              type="button"
              class="btn btn-success"
              v-on:click="openPlace(feature, false)"
            >
              <i class="fa fa-fw fa-plus"></i> {{ _('Add') }}
            </button>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import components from '~/lib/components'
import permissions from '~/lib/permissions'
import settingshelpers from '~/lib/settingshelpers'
import ModalUiFeaturePlace from '~/components/modals/ModalUiFeaturePlace.vue'

export default {
  data () {
    return {
      features: [],
      destinations: []
    }
  },
  mixins: [components, permissions, settingshelpers],
  computed: {
    ...mapGetters(['clientIdNumber']),
    hasOtherClients () {
      return this.destinations.length > 1
    }
  },
  methods: {
    async load () {
      if (!this.isFeatureEnabled('uifeatures')) {
        this.features = []
        this.destinations = []
        return
      }
      this.loading = true
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/uifeatures.json?client_id=' + (this.clientIdNumber || 0) + '&apikey=' + this.apiKey + this.versionStr)
      } catch (e) {
        this.loading = false
        return this.showError(e)
      }
      this.features = Array.isArray(response.data) ? response.data : []
      this.loading = false
      await this.loadDestinations()
    },
    async loadDestinations () {
      if (!this.canModifyLayouts || !this.features.length) {
        this.destinations = []
        return
      }
      try {
        const first = this.features[0]
        const response = await axios.get(this.baseDomain + this.shopId + '/uifeatures/' + first.id + '.json?destinations=1&client_id=' + (this.clientIdNumber || 0) + '&apikey=' + this.apiKey + this.versionStr)
        this.destinations = Array.isArray(response.data.destinations) ? response.data.destinations : []
      } catch (e) {
        this.destinations = []
      }
    },
    openPlace (feature, pickOtherClient) {
      this.showModal('uifeatureplace', ModalUiFeaturePlace, {
        props: {
          recipeId: feature.id,
          recipeTitle: feature.title,
          recipeIcon: feature.icon || '',
          pickOtherClient
        },
        events: {
          placed: this.afterPlace
        },
        handlers: {},
        width: 640
      })
    },
    reloadOnVersionChange () {
      return this.load()
    },
    afterPlace (placed) {
      this.showNotification(this._('Added to the user interface.'))
      const clients = placed && Array.isArray(placed.clients) ? placed.clients : []
      const onThisUi = clients.some(client => client.id === this.clientIdNumber)
      if (onThisUi && placed.layout_id) {
        this.EventBus.$emit('gotoLayout', { id: placed.layout_id })
        return
      }
      this.load()
    }
  },
  created () {
    this.load()
  }
}
</script>

<style scoped>
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.feature-card {
  display: flex;
  flex-direction: column;
  min-height: 220px;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
}
.feature-card.is-added {
  background: #f7f7f7;
  color: #777;
}
.feature-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #f0f7fc;
  color: #337ab7;
}
.feature-card.is-added .feature-card__icon {
  background: #eee;
  color: #888;
}
.feature-card__title {
  margin: 0 0 8px;
  font-size: 18px;
}
.feature-card__description {
  flex: 1 1 auto;
  margin: 0 0 16px;
  color: #555;
}
.feature-card.is-added .feature-card__description {
  color: #888;
}
.feature-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
</style>
